import BN from 'bn.js';
import * as encUtils from 'enc-utils';

import * as compatible from './core-sdk-compatible/crypto';
import * as legacy from './crypto';
import { getStarkPublicKeyFromImx } from './fetchUser';

/**
 * Get a Stark Private Key from a given seed and path.
 * @param seed
 * @param path
 * @param ethAddress
 * @returns Stark Private Key
 */
export async function getKeyFromPath(
  seed: string,
  path: string,
  ethAddress: string,
): Promise<string> {
  const privateKeySeed = compatible.getPrivateKeyFromPath(seed, path);
  const starkPrivateKey = compatible.grindKey(privateKeySeed);

  // The following logic is added in response to bug found in DX-2167 and DX-2184.
  // To provide a backwards compatible way to fetch keys across link/js SDK and Core SDK.

  // Note: The issue we are addressing here is that if the hashKeyWithIndex value is above the limit, then we perform
  //     hash again with index until it comes below the limit. But for the first time the index is not incremented in
  //     imx-sdk-js where as it was in core-sdk that was the difference. For this reason it would have worked for cases
  //     where the hashed value was less than the limit and fails only when it is above the limit.
  //     Refer, https://immutable.atlassian.net/browse/DX-2167

  // Same code as grindKey, required to check if the generated private key
  // goes above the limit when hashed for first time to identify if we need to do backwards
  // compatibility check:

  // The bug only exists if the hashed value of given seed is above the stark curve limit.
  if (!compatible.checkIfHashedKeyIsAboveLimit(privateKeySeed)) {
    return starkPrivateKey;
  }

  // Check if the generated stark public key matches with the existing account value for that user.
  // We are only validating for Production environment.
  // For Sandbox account/key mismatch, solution is to discard the old account and create a new one.
  const imxResponse = await getStarkPublicKeyFromImx(ethAddress);
  // If the account is not found or account matches we just return the key pair at the end of this method.
  // Only need to so alternative method if the account is found but the stark public key does not match.

  if (imxResponse === undefined) {
    throw new Error('Error fetching stark public key from IMX');
  }

  // If the account is not found it is a new account, just return the Stark Private Key that is generated by grindKey function.
  if (imxResponse.accountNotFound) {
    return starkPrivateKey;
  }

  const registeredStarkPublicKeyBN = new BN(
    encUtils.removeHexPrefix(imxResponse.starkPublicKey),
    16,
  );

  // If the user account matches with generated stark public key user, just return Stark Private Key.
  let starkPublicKey = getStarkPublicKeyFromKey(starkPrivateKey);
  if (
    registeredStarkPublicKeyBN.eq(
      new BN(encUtils.removeHexPrefix(starkPublicKey), 16),
    )
  ) {
    return starkPrivateKey;
  }

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // This is backwards compatible crypto (core-sdk) version 2.0.1

  // If we are here, we found the account but did not match with the recorded user account.
  // Lets try to use grindKeyV201 method from backwards compatible to generate a key and see if that matches.
  const starkPrivateKeyCompatibleV1 = compatible.grindKeyV201(privateKeySeed);
  starkPublicKey = getStarkPublicKeyFromKey(starkPrivateKeyCompatibleV1);
  if (
    registeredStarkPublicKeyBN.eq(
      new BN(encUtils.removeHexPrefix(starkPublicKey), 16),
    )
  ) {
    return starkPrivateKeyCompatibleV1;
  }

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // This section is legacy crypto, compatible with imx-sdk-js released before 1.43.5

  const privateKeyString = legacy.getPrivateKeyFromPath(seed, path);
  const starkPrivateKeyLegacy = legacy.grindKey(privateKeyString);
  starkPublicKey = getStarkPublicKeyFromKey(starkPrivateKeyLegacy);
  if (
    registeredStarkPublicKeyBN.eq(
      new BN(encUtils.removeHexPrefix(starkPublicKey), 16),
    )
  ) {
    return starkPrivateKeyLegacy;
  }

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  // Account is found, but did not match with stark public keys generated by either grindKey or grindKeyV1 method.
  // Will have to contact support for further investigation.
  throw new Error(
    'Can not deterministically generate stark private key - please contact support',
  );
}

export function getStarkPublicKeyFromKey(starkPrivateKey: string): string {
  const keyPair = legacy.getKeyPair(starkPrivateKey);
  return encUtils.sanitizeHex(
    legacy.getXCoordinate(legacy.getStarkPublicKey(keyPair)),
  );
}
